import React from 'react';
import { Container, Typography, Box, Paper, Grid, Button, Divider, Card, CardContent, CardActionArea } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import StorageIcon from '@mui/icons-material/Storage';
import SchoolIcon from '@mui/icons-material/School';
import EmojiPeopleIcon from '@mui/icons-material/EmojiPeople';
import GavelIcon from '@mui/icons-material/Gavel';
import SecurityIcon from '@mui/icons-material/Security';
import VerifiedUserIcon from '@mui/icons-material/VerifiedUser';

const RsePage = () => {
    const navigate = useNavigate();

    const sections = [
        {
            title: "Conditions Générales d'Utilisation",
            icon: <GavelIcon sx={{ fontSize: 40, color: '#667eea' }} />,
            description: "Les règles encadrant l'utilisation de notre plateforme.",
            link: "/of-cgu"
        },
        {
            title: "Politique de Confidentialité (RGPD)",
            icon: <SecurityIcon sx={{ fontSize: 40, color: '#764ba2' }} />,
            description: "Comment nous protégeons et traitons vos données personnelles.",
            link: "/rgpd"
        },
        {
            title: "Règlement Apprenant",
            icon: <VerifiedUserIcon sx={{ fontSize: 40, color: '#2193b0' }} />,
            description: "Les engagements et devoirs de l'apprenant au sein de la formation.",
            link: "/learner-rules"
        }
    ];

    return (
        <Box sx={{
            minHeight: '100vh',
            bgcolor: '#f5f7fa',
            background: 'linear-gradient(135deg, #f5f7fa 0%, #e4e9f2 100%)',
            py: 6
        }}>
            <Container maxWidth="lg">
                {/* Header / Navigation */}
                <Button
                    startIcon={<ArrowBackIcon />}
                    onClick={() => navigate('/login')}
                    sx={{
                        mb: 4,
                        textTransform: 'none',
                        color: '#555',
                        '&:hover': { bgcolor: 'rgba(0,0,0,0.05)' }
                    }}
                >
                    Retour à la connexion
                </Button>

                {/* Hero Section */}
                <Paper
                    elevation={0}
                    sx={{
                        p: { xs: 4, md: 8 },
                        borderRadius: 6,
                        background: 'linear-gradient(120deg, #667eea 0%, #764ba2 100%)',
                        color: 'white',
                        mb: 6,
                        textAlign: 'center',
                        position: 'relative',
                        overflow: 'hidden',
                        boxShadow: '0 20px 40px rgba(102, 126, 234, 0.25)'
                    }}
                >
                    <Box sx={{ position: 'absolute', top: -100, left: -50, width: 300, height: 300, borderRadius: '50%', background: 'rgba(255,255,255,0.1)', filter: 'blur(50px)' }} />
                    <Box sx={{ position: 'absolute', bottom: -50, right: -50, width: 200, height: 200, borderRadius: '50%', background: 'rgba(255,255,255,0.1)', filter: 'blur(40px)' }} />

                    <Typography variant="overline" sx={{ letterSpacing: 2, opacity: 0.8, fontWeight: 600 }}>
                        Responsabilité Sociétale des Entreprises
                    </Typography>
                    <Typography variant="h2" component="h1" sx={{ fontWeight: 800, mt: 2, mb: 3, fontFamily: 'Quicksand, sans-serif' }}>
                        Notre Engagement RSE
                    </Typography>
                    <Typography variant="h6" sx={{ maxWidth: 700, mx: 'auto', opacity: 0.9, fontWeight: 400, lineHeight: 1.6 }}>
                        Chez Edu-JALM, nous croyons en une formation éthique, durable et inclusive.
                        Nous nous engageons à respecter les normes les plus strictes en matière de responsabilité sociale et environnementale.
                    </Typography>
                </Paper>

                {/* Values Section */}
                <Grid container spacing={4} sx={{ mb: 8 }}>
                    <Grid size={{ xs: 12, md: 4 }}>
                        <Box sx={{ textAlign: 'center', p: 3 }}>
                            <Box sx={{ display: 'inline-flex', p: 2, borderRadius: '50%', bgcolor: 'rgba(33, 150, 243, 0.1)', mb: 2 }}>
                                <StorageIcon sx={{ fontSize: 40, color: '#2196F3' }} />
                            </Box>
                            <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>Souveraineté des Données</Typography>
                            <Typography variant="body2" color="text.secondary">
                                Vos données sont hébergées exclusivement en France, garantissant sécurité et conformité RGPD.
                            </Typography>
                        </Box>
                    </Grid>
                    <Grid size={{ xs: 12, md: 4 }}>
                        <Box sx={{ textAlign: 'center', p: 3 }}>
                            <Box sx={{ display: 'inline-flex', p: 2, borderRadius: '50%', bgcolor: 'rgba(76, 175, 80, 0.1)', mb: 2 }}>
                                <SchoolIcon sx={{ fontSize: 40, color: '#4CAF50' }} />
                            </Box>
                            <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>Expertise Pédagogique</Typography>
                            <Typography variant="body2" color="text.secondary">
                                Une plateforme conçue par des formateurs pour des formateurs, au service de l'excellence éducative.
                            </Typography>
                        </Box>
                    </Grid>
                    <Grid size={{ xs: 12, md: 4 }}>
                        <Box sx={{ textAlign: 'center', p: 3 }}>
                            <Box sx={{ display: 'inline-flex', p: 2, borderRadius: '50%', bgcolor: 'rgba(255, 152, 0, 0.1)', mb: 2 }}>
                                <EmojiPeopleIcon sx={{ fontSize: 40, color: '#FF9800' }} />
                            </Box>
                            <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>Priorité Apprenant</Typography>
                            <Typography variant="body2" color="text.secondary">
                                Nous plaçons les besoins et la réussite de l'apprenant au cœur de toutes nos innovations.
                            </Typography>
                        </Box>
                    </Grid>
                </Grid>

                <Divider sx={{ mb: 6 }} />

                {/* Legal Documents Section */}
                <Typography variant="h4" sx={{ fontWeight: 700, mb: 4, textAlign: 'center', color: '#2d3748' }}>
                    Documents Légaux
                </Typography>

                <Grid container spacing={3}>
                    {sections.map((section, index) => (
                        <Grid size={{ xs: 12, md: 4 }} key={index}>
                            <Card
                                elevation={0}
                                sx={{
                                    height: '100%',
                                    borderRadius: 4,
                                    border: '1px solid rgba(0,0,0,0.05)',
                                    transition: 'transform 0.2s, box-shadow 0.2s',
                                    '&:hover': {
                                        transform: 'translateY(-5px)',
                                        boxShadow: '0 12px 24px rgba(0,0,0,0.1)'
                                    }
                                }}
                            >
                                <CardActionArea onClick={() => navigate(section.link)} sx={{ height: '100%', p: 2 }}>
                                    <CardContent sx={{ textAlign: 'center' }}>
                                        <Box sx={{ mb: 2 }}>
                                            {section.icon}
                                        </Box>
                                        <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                                            {section.title}
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary">
                                            {section.description}
                                        </Typography>
                                    </CardContent>
                                    <Box sx={{ textAlign: 'center', pb: 2 }}>
                                        <Typography variant="button" sx={{ color: '#667eea', fontWeight: 700 }}>
                                            Consulter
                                        </Typography>
                                    </Box>
                                </CardActionArea>
                            </Card>
                        </Grid>
                    ))}
                </Grid>
            </Container>
        </Box>
    );
};

export default RsePage;
